import React, {
  DetailedHTMLProps,
  HTMLAttributes,
  ReactElement,
  ReactHTML,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { autoTextSize, Options } from './auto-text-size-standalone';

/**
 * Make text fit container, prevent overflow and underflow.
 */
export function AutoTextSize({
  mode,
  minFontSizePx,
  maxFontSizePx,
  fontSizePrecisionPx,
  as: Comp,
  children,
  flexGrow,
  ...rest
}: Options & {
  as?: keyof ReactHTML;
} & DetailedHTMLProps<
    HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >): ReactElement {
  const updateTextSizeRef = useRef<ReturnType<typeof autoTextSize>>();

  useEffect(() => updateTextSizeRef.current?.(), [children]);

  const refCallback = useCallback(
    (innerEl: HTMLElement | null) => {
      updateTextSizeRef.current?.disconnect();

      const containerEl = innerEl?.parentElement;
      if (!innerEl || !containerEl) return;

      updateTextSizeRef.current = autoTextSize({
        innerEl,
        containerEl,
        mode,
        flexGrow,
        minFontSizePx,
        maxFontSizePx,
        fontSizePrecisionPx,
      });
    },
    [mode, flexGrow, minFontSizePx, maxFontSizePx, fontSizePrecisionPx],
  );

  if (typeof Comp === 'string') {
    return React.createElement(Comp, { ref: refCallback, ...rest }, children);
  } else {
    return (
      <div ref={refCallback} {...rest}>
        {children}
      </div>
    );
  }
}
